/**
 * @file The bake's constants, as data. Mirrors the `BakeConfig` typedef in
 * `engine/types.js` field for field — that JSDoc block is the contract, this
 * file is its runtime value. `dev/scenes/daynight.js`'s hand-written `BAKE`
 * constant diffs clean against {@link DAY_DEFAULTS} (same names, same numbers),
 * which is how we know the real bake reproduces T2's look reference.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TWO FORMULAS (everything else here is a knob on them)
 * ─────────────────────────────────────────────────────────────────────────────
 *   day   = albedo * ( sun*1.35*max(N.L,0)*sunVis
 *                    + hemi(n)*0.55*ao + bounce*0.22*ao + 0.05 )
 *   night = albedo * ( moon*0.22*max(N.M,0)*moonVis*0.5
 *                    + nightHemi*0.16*ao + SUM(emitters)*ao + 0.02 )
 *
 * ZERO three.js imports — colours are plain `[r,g,b]`, directions plain
 * `{x,y,z}`, because a config crosses the worker boundary by structured clone.
 * `mergeConfig` accepts `THREE.Color` / `THREE.Vector3` on the way IN, though.
 *
 * @module engine/bake/BakeConfig
 */

/** @typedef {import('../types.js').BakeConfig} BakeConfig */
/** @typedef {import('../types.js').AoConfig} AoConfig */

/**
 * @param {any} v
 * @param {[number,number,number]} fallback
 * @returns {[number,number,number]}
 */
function rgb(v, fallback) {
  if (v == null) return [fallback[0], fallback[1], fallback[2]];
  if (Array.isArray(v)) return [v[0], v[1], v[2]];
  if (typeof v === 'number') return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
  if (typeof v.r === 'number') return [v.r, v.g, v.b];
  return [fallback[0], fallback[1], fallback[2]];
}

/**
 * @param {any} v
 * @param {{x:number,y:number,z:number}} fallback
 * @returns {{x:number,y:number,z:number}} Normalised.
 */
function dir(v, fallback) {
  let x = fallback.x;
  let y = fallback.y;
  let z = fallback.z;
  if (Array.isArray(v)) {
    x = v[0];
    y = v[1];
    z = v[2];
  } else if (v && typeof v.x === 'number') {
    x = v.x;
    y = v.y;
    z = v.z;
  }
  const l = Math.hypot(x, y, z) || 1;
  return { x: x / l, y: y / l, z: z / l };
}

/**
 * The baked sun. A LEVEL CONSTANT: elevation ~42 degrees, azimuth on the camera
 * side so roofs AND camera-facing walls catch light. Nothing about the shading
 * moves after the bake — that is what buys the whole no-shadow-map look.
 * @type {{x:number,y:number,z:number}}
 */
export const SUN_DIR = dir({ x: 0.42, y: 0.67, z: 0.61 }, { x: 0, y: 1, z: 0 });

/**
 * The baked moon: the opposite arc, still high enough to model roof planes so
 * the night silhouette does not go flat.
 *
 * ⚠ EXTENSION beyond `types.js -> BakeConfig`, which specifies `moonColor` /
 * `moonIntensity` / `moonVisScale` but no direction. The night term needs an `L`
 * and this is the value `dev/scenes/daynight.js` already shades with.
 * @type {{x:number,y:number,z:number}}
 */
export const MOON_DIR = dir({ x: -0.42, y: 0.6, z: -0.61 }, { x: 0, y: 1, z: 0 });

/**
 * The warm street/window glow colour every city emitter should use, verified
 * against T2's look reference. Not part of `BakeConfig` — an emitter carries its
 * own colour — but levels want one canonical value.
 * @type {[number,number,number]}
 */
export const STREET_GLOW = [1.0, 0.7, 0.36];

/**
 * City defaults. Every number here is the `types.js` documented default.
 * @type {Readonly<BakeConfig & {moonDir: {x:number,y:number,z:number}, moonRays: number, sunMaxDist: number, gradeAmount: number, gradeColor: [number,number,number], probeSunScale: number}>}
 */
export const DAY_DEFAULTS = Object.freeze({
  // ── sun ──
  sunDir: Object.freeze({ ...SUN_DIR }),
  sunColor: Object.freeze([1.0, 0.94, 0.82]),
  sunIntensity: 1.35,
  sunConeDeg: 3.5,
  /**
   * Rays per vertex through the `sunConeDeg` cone — this is the PENUMBRA
   * sampler, and it is the cheapest quality lever the bake has. At 3 a soft
   * edge quantises to four states (0, 1/3, 2/3, 1) and reads as banding on
   * every long shadow; 16 resolves the ramp. It costs bake TIME only — no
   * vertices, no memory, no frame cost — and only the sun/moon visibility
   * portion of the bake scales with it, not AO.
   *
   * ⚠ It is NOT the shadow RESOLUTION. A shadow edge can still only land on a
   * vertex, so `tessellateForBake`'s `maxEdge` (4.5 m in the city) is what
   * bounds shadow SHAPE. Raising rays past ~16 sharpens nothing.
   */
  sunRays: 16,
  /** EXTENSION: caps sun-visibility ray length. A 30 m tower at 42 deg elevation
   *  throws a ~33 m shadow, so 90 m is generous — and it is the single biggest
   *  lever on bake time (an uncapped ray marches the whole city). */
  sunMaxDist: 90,
  /** EXTENSION: vertices BELOW this Y are INTERIOR — their sun becomes a
   *  straight-up frozen skylight and (under `splitDay`) their `sunTint` bakes
   *  to zero, exempting them from the moving sun entirely. The city passes -50
   *  (its rooms sit at -100). Default: no vertex qualifies. */
  interiorBelowY: -Infinity,

  // ── sky / bounce ──
  hemiSky: Object.freeze([0.55, 0.66, 0.86]),
  hemiGround: Object.freeze([0.34, 0.3, 0.26]),
  hemiIntensity: 0.55,
  bounceColor: Object.freeze([0.5, 0.44, 0.36]),
  bounceIntensity: 0.22,
  ambientFloor: 0.05,

  // ── ambient occlusion ──
  ao: Object.freeze({ rays: 14, radius: 5, strength: 0.85, floor: 0.18 }),

  // ── night ──
  moonDir: Object.freeze({ ...MOON_DIR }),
  moonColor: Object.freeze([0.62, 0.72, 1.0]),
  moonIntensity: 0.22,
  moonVisScale: 0.5,
  /** EXTENSION: moon visibility is a soft wash, one ray is plenty. */
  moonRays: 1,
  nightHemi: Object.freeze([0.14, 0.18, 0.32]),
  nightHemiIntensity: 0.16,
  nightFloor: 0.02,
  emitterIntensity: 1,

  // ── albedo ──
  tintJitter: 0.06,
  gradeScale: 0.02,
  /** EXTENSION: strength of the noise-driven moss/rust grading. DEFAULT 0 = off,
   *  so a stock bake is a pure lighting operation and `GeoBuilder`'s albedo
   *  survives byte-exact. Levels that want weathering turn it up (0.1 - 0.25). */
  gradeAmount: 0,
  /** EXTENSION: what `gradeAmount` grades TOWARD (damp moss/rust). */
  gradeColor: Object.freeze([0.42, 0.44, 0.3]),

  // ── probes ──
  /** EXTENSION: `LightProbeGrid` has no surface normal, so the sun term is
   *  averaged over a standing actor rather than using max(N.L,0). */
  probeSunScale: 0.55,

  // ── machinery ──
  dedupQuant: 0.02,
  useWorker: true,
});

/**
 * Small props (crates, lamps, signs): a 3 m AO radius so a barrel does not go
 * looking for occluders across the street.
 * @type {Readonly<typeof DAY_DEFAULTS>}
 */
export const PROP = Object.freeze(mergeConfig(DAY_DEFAULTS, {
  ao: { radius: 3 },
  sunMaxDist: 40,
}));

/**
 * Creatures: 1.2 m AO radius (self-occlusion only — the point is crevices in a
 * blob, not the room it stands in) and no sun rays, because the mesh is baked
 * once and then instanced all over the level.
 * @type {Readonly<typeof DAY_DEFAULTS>}
 */
export const CREATURE = Object.freeze(mergeConfig(DAY_DEFAULTS, {
  ao: { radius: 1.2, strength: 0.9, floor: 0.22 },
  sunRays: 1,
  sunMaxDist: 6,
  useWorker: false,
}));

/**
 * Normalises + merges a partial config over a base. Deep for `ao` only (the one
 * nested object), because a caller passing `{ao:{radius:3}}` means "radius 3,
 * keep the rest", never "drop rays/strength/floor".
 *
 * Colours accept `[r,g,b]`, `0xrrggbb`, `THREE.Color`; directions accept
 * `[x,y,z]`, `{x,y,z}`, `THREE.Vector3` and are RE-NORMALISED. The result is a
 * plain frozen object with no class instances, safe to `postMessage`.
 *
 * @param {Partial<BakeConfig>|null} [base]
 * @param {Partial<BakeConfig>|null} [over]
 * @returns {any} A full config.
 */
export function mergeConfig(base, over) {
  const b = base || {};
  const o = over || {};
  const D = /** @type {any} */ (DAY_DEFAULTS);
  /** @param {string} k */
  const num = (k) => {
    const v = o[k] ?? b[k] ?? D[k];
    return typeof v === 'number' ? v : D[k];
  };
  /** @param {string} k */
  const col = (k) => rgb(o[k] ?? b[k], D[k]);
  /** @param {string} k */
  const vec = (k) => dir(o[k] ?? b[k], D[k]);

  const baseAo = /** @type {AoConfig} */ (b.ao || D.ao);
  const overAo = /** @type {AoConfig} */ (o.ao || {});
  const ao = Object.freeze({
    rays: Math.max(1, Math.round(overAo.rays ?? baseAo.rays ?? D.ao.rays)),
    radius: overAo.radius ?? baseAo.radius ?? D.ao.radius,
    strength: overAo.strength ?? baseAo.strength ?? D.ao.strength,
    floor: overAo.floor ?? baseAo.floor ?? D.ao.floor,
  });

  return Object.freeze({
    /** Marker so hot paths can skip re-normalising an already-merged config. */
    __merged: true,
    sunDir: Object.freeze(vec('sunDir')),
    sunColor: Object.freeze(col('sunColor')),
    sunIntensity: num('sunIntensity'),
    sunConeDeg: num('sunConeDeg'),
    sunRays: Math.max(1, Math.round(num('sunRays'))),
    sunMaxDist: num('sunMaxDist'),
    interiorBelowY: num('interiorBelowY'),

    hemiSky: Object.freeze(col('hemiSky')),
    hemiGround: Object.freeze(col('hemiGround')),
    hemiIntensity: num('hemiIntensity'),
    bounceColor: Object.freeze(col('bounceColor')),
    bounceIntensity: num('bounceIntensity'),
    ambientFloor: num('ambientFloor'),

    ao,

    moonDir: Object.freeze(vec('moonDir')),
    moonColor: Object.freeze(col('moonColor')),
    moonIntensity: num('moonIntensity'),
    moonVisScale: num('moonVisScale'),
    moonRays: Math.max(0, Math.round(num('moonRays'))),
    nightHemi: Object.freeze(col('nightHemi')),
    nightHemiIntensity: num('nightHemiIntensity'),
    nightFloor: num('nightFloor'),
    emitterIntensity: num('emitterIntensity'),

    tintJitter: num('tintJitter'),
    gradeScale: num('gradeScale'),
    gradeAmount: num('gradeAmount'),
    gradeColor: Object.freeze(col('gradeColor')),

    probeSunScale: num('probeSunScale'),

    dedupQuant: num('dedupQuant'),
    useWorker: (o.useWorker ?? b.useWorker ?? D.useWorker) !== false,

    /**
     * Emit the day channel's direction-free half (`ambient`) and its sun tint
     * alongside the combined `color`, so `bake/sunKeys.js` can drive the sun
     * term at runtime. OPT-IN: it widens the solve store and allocates two more
     * output arrays, and only a level with a moving sun has any use for it.
     * `color` is byte-identical either way.
     */
    splitDay: (o.splitDay ?? b.splitDay ?? false) === true,
  });
}
